import type { Components, Theme } from "@mui/material/styles";

/**
 * Input text colours.
 *
 * MUI colours the text a user types from palette.text.primary — which in this
 * app is #7e7e7e, a mid grey (see appcolors.ts: `--bs-text-color`) — and then
 * paints the placeholder as that SAME colour at ~42% opacity. The two end up
 * only a shade apart, so real input reads as placeholder text.
 *
 * Rather than repaint text.primary (which every heading and body copy in the
 * app also hangs off), pin the colours on the input slots themselves: typed and
 * selected values go dark, placeholders stay deliberately light.
 *
 * This covers every text field, select, autocomplete and date field in the app,
 * because they all render their value through .MuiInputBase-input.
 */
export const inputComponents = (mode: "light" | "dark"): Components<Theme> => {
  const value = mode === "light" ? "#111827" : "#E5E7EB";
  const placeholder = mode === "light" ? "#9CA3AF" : "#8A93A0";
  // Disabled stays muted but must remain legible. MUI greys disabled fields via
  // -webkit-text-fill-color, so setting `color` alone would have no effect.
  const disabled = mode === "light" ? "#6B7280" : "#9CA3AF";
  // Chrome paints its own yellow autofill background; keep the field's surface.
  const surface = mode === "light" ? "#ffffff" : "#282828";

  return {
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: value,
          "&::placeholder": {
            color: placeholder,
            // Browsers apply a ~0.5 default opacity on top of the placeholder
            // colour; without this the grey washes out further still.
            opacity: 1,
            WebkitTextFillColor: placeholder,
          },
          "&.Mui-disabled": {
            color: disabled,
            WebkitTextFillColor: disabled,
          },
          "&:-webkit-autofill": {
            WebkitTextFillColor: value,
            caretColor: value,
            WebkitBoxShadow: `0 0 0 100px ${surface} inset`,
          },
        },
      },
    },
    // A Select renders its value into a <div>, not an <input>; that node carries
    // .MuiSelect-select, so it needs colouring in its own right.
    MuiSelect: {
      styleOverrides: {
        select: {
          color: value,
          "&.Mui-disabled": {
            color: disabled,
            WebkitTextFillColor: disabled,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: { color: value },
      },
    },
  };
};
